import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { professionalProfileApi, resumeApi } from '../../services/api';
import MBTI_PRESETS from '../../data/mbti_presets.json';
import ResumeSelector from '../resumes/ResumeSelector';

const VISA_OPTIONS = ['US_Citizen','Green_Card','H1B','F1_OPT','J1','Other'];
const REMOTE_OPTIONS = ['Remote','Hybrid','On-site','Flexible'];
const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','Other'
];
const MBTI_OPTIONS = ['INTJ','INTP','ENTJ','ENTP','INFJ','INFP','ENFJ','ENFP','ISTJ','ISFJ','ESTJ','ESFJ','ISTP','ISFP','ESTP','ESFP','Other'];
const INDUSTRIES = [
  'Technology','Healthcare','Finance','Education','Manufacturing','Retail','Real Estate','Transportation',
  'Energy','Media','Government','Non-profit','Consulting','Legal','Agriculture','Construction','Other'
];
const COMPANY_SIZES = ['Startup (1-50)','Small (51-200)','Medium (201-1000)','Large (1001-5000)','Enterprise (5000+)'];
const EMPLOYMENT_TYPES = ['Full-time','Part-time','Contract','Freelance','Internship','Temporary'];
const CAREER_LEVELS = ['Entry Level','Associate','Mid-Level','Senior','Lead','Principal','Manager','Director','VP','C-Level'];
const BENEFITS = ['Health Insurance','401k/Retirement','Flexible Schedule','Remote Work','Professional Development','Stock Options','Unlimited PTO','Parental Leave','Other'];
// presets now loaded from JSON; fields will auto-populate and be locked

const defaultState = {
  step: 1,
  first: {
    visa_status: '',
    location_preference: { current_location: '', preferred_locations: [], willing_to_relocate: false, relocation_assistance_needed: false, commute_preference: { max_commute_time: 60, transportation: [] } },
    career_preference: { desired_roles: [], industries: [], company_size: [], employment_type: [], salary_range: { min: null, max: null, currency: 'USD', negotiable: true }, benefits_priorities: [], career_level: '', remote_preference: '' },
    personality: { mbti: '', traits: [], work_style: '', leadership_style: '' },
  },
  hasExistingResume: false,
  // Cache uploaded resume content to persist across navigation
  cachedResumeContent: null,
  uploadedResumeFileName: null,
  selectedResume: null, // For choosing from existing resumes
  resumeOption: 'uploadNew', // Options: 'useExisting', 'selectExisting', 'uploadNew' - will be set correctly in useEffect
  
  // ENHANCED STATE FOR NEW 6-STEP PROCESS
  universityDetected: null,
  universityInDatabase: false,
  selectedCourses: [],
  selectedCourseDetails: [],
  courseOptions: [],
  
  // Enhanced directions (6 instead of 4)
  careerDirections: [],
  primaryDirection: null,
  followupAnswers: {},
  followupContextSelections: {},
  showAdditionalDirections: false, // Show direction selection after profile save
  
  // Legacy fields for backward compatibility
  directions: null,
  chosenDirection: '',
  professional_goals: '',
  analysis: null,
  selectedSkills: [],
  selectedProjects: [],
  profileId: null,
};

export default function ProfessionalProfileCollection({ open, onClose, profile, onProfileUpdate, reloadProfiles }) {
  const navigate = useNavigate();
  const [state, setState] = useState(defaultState);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCareerDirectionModal, setShowCareerDirectionModal] = useState(false);
  const pendingTimerRef = useRef(null);

  useEffect(() => {
    return () => { if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current); };
  }, []);

  const detectExistingResume = async () => {
    try {
      const resumes = await resumeApi.getAllResumes();
      const hasResume = resumes?.data?.length > 0;
      setState(s => ({ ...s, hasExistingResume: hasResume }));
      return hasResume;
    } catch (e) {
      console.error(e);
      return false;
    }
  };

  // Helper function to check if profile has usable data
  const hasUsableProfileData = (profile) => {
    if (!profile) return false;
    if (Array.isArray(profile) && profile.length === 0) return false;
    if (Array.isArray(profile)) return false; // Profile should be an object, not array
    return profile.background && (
      (profile.background.education && profile.background.education.length > 0) ||
      (profile.background.experience && profile.background.experience.length > 0)
    );
  };

  useEffect(() => {
    if (open) {
      // Prefill with existing profile data if available
      const prefilledState = {
        ...defaultState,
        first: {
          ...defaultState.first,
          // Prefill visa status
          visa_status: profile?.visa_status || defaultState.first.visa_status,
          // Prefill location preferences
          location_preference: {
            ...defaultState.first.location_preference,
            current_location: profile?.location || defaultState.first.location_preference.current_location,
            preferred_locations: profile?.location_preference?.preferred_locations || defaultState.first.location_preference.preferred_locations,
            willing_to_relocate: profile?.location_preference?.willing_to_relocate || defaultState.first.location_preference.willing_to_relocate,
            relocation_assistance_needed: profile?.location_preference?.relocation_assistance_needed || defaultState.first.location_preference.relocation_assistance_needed,
            commute_preference: {
              ...defaultState.first.location_preference.commute_preference,
              ...profile?.location_preference?.commute_preference
            }
          },
          // Prefill career preferences
          career_preference: {
            ...defaultState.first.career_preference,
            desired_roles: profile?.career_preference?.desired_roles || defaultState.first.career_preference.desired_roles,
            industries: profile?.career_preference?.industries || defaultState.first.career_preference.industries,
            company_size: profile?.career_preference?.company_size || defaultState.first.career_preference.company_size,
            employment_type: profile?.career_preference?.employment_type || defaultState.first.career_preference.employment_type,
            remote_preference: profile?.career_preference?.remote_preference || defaultState.first.career_preference.remote_preference,
            career_level: profile?.career_preference?.career_level || defaultState.first.career_preference.career_level,
            salary_range: {
              ...defaultState.first.career_preference.salary_range,
              ...profile?.career_preference?.salary_range
            },
            benefits_priorities: profile?.career_preference?.benefits_priorities || defaultState.first.career_preference.benefits_priorities
          },
          // Prefill personality data
          personality: {
            ...defaultState.first.personality,
            mbti: profile?.personality?.mbti || defaultState.first.personality.mbti,
            traits: profile?.personality?.traits || defaultState.first.personality.traits,
            work_style: profile?.personality?.work_style || defaultState.first.personality.work_style,
            leadership_style: profile?.personality?.leadership_style || defaultState.first.personality.leadership_style
          }
        },
        // Prefill professional goals (combined and individual fields)
        professional_goals: profile?.professional_goals ? 
          [
            ...(profile.professional_goals.short_term || []).map(goal => `Short-term: ${goal}`),
            ...(profile.professional_goals.long_term || []).map(goal => `Long-term: ${goal}`),
            ...(profile.professional_goals.learning_interests || []).map(interest => `Learning: ${interest}`)
          ].join('\n') : '',
        short_term_goals: Array.isArray(profile?.professional_goals?.short_term) ? (profile.professional_goals.short_term || []).join('\n') : (profile?.professional_goals?.short_term || ''),
        long_term_goals: Array.isArray(profile?.professional_goals?.long_term) ? (profile.professional_goals.long_term || []).join('\n') : (profile?.professional_goals?.long_term || ''),
        // Set profile ID if available
        profileId: profile?._id || null
      };
      
      setState(prefilledState);
      let cancelled = false;
      detectExistingResume()
        .then((hasExisting) => {
          if (!cancelled) {
            setState(s => ({
              ...s,
              resumeOption: hasUsableProfileData(profile) ? 'useExisting' : hasExisting ? 'selectExisting' : 'uploadNew'
            }));
          }
        })
        .catch(() => {});
      return () => { cancelled = true; };
    }
  }, [open]);

  // Load courses when step 3 is reached
  useEffect(() => {
    if (state.step === 3 && state.courseOptions?.length === 0 && state.universityDetected) {
      queryCourses();
    }
  }, [state.step]);

  const next = () => setState(s => ({ ...s, step: s.step + 1 }));
  const prev = () => setState(s => ({ 
    ...s, 
    step: (() => {
      const currentStep = s.step;
      // Smart back navigation: if we're on step 4 and university is not in database, go back to step 2 (skip step 3)
      if (currentStep === 4 && !s.universityInDatabase) {
        return 2;
      }
      // If we're on step 5 and university is not in database, go back to step 4
      if (currentStep === 5 && !s.universityInDatabase && s.step > 4) {
        return 4;
      }
      // Default behavior
      return Math.max(1, currentStep - 1);
    })()
  }));

  const saveFirstStepToProfile = () => {
    const updates = { ...(profile || {}), ...state.first };
    onProfileUpdate(updates);
  };

  const ensureProfile = async (baseData = null) => {
    // Ensure there is a backend profile and return its id
    let pid = state.profileId || profile?._id;
    if (pid) return pid;
    const payload = baseData || { ...(profile || {}), ...state.first };
    const created = await professionalProfileApi.createProfile(payload);
    const newId = created?.data?.id;
    setState(s => ({ ...s, profileId: newId }));
    return newId;
  };

  const handleResumeUpload = async (file) => {
    try {
      setUploading(true);
      
      // Use the new backend endpoint that handles everything
      const response = await professionalProfileApi.uploadResumeAndIntegrateWithProfile(file, state.first, state.selectedResume?.id);
      
      if (response?.data?.success) {
        const { profile_id, profile, parsed_content, university_detected, university_in_database } = response.data;
        
        // Update the frontend state with the integrated profile
        onProfileUpdate(profile);
        setState(s => ({ 
          ...s, 
          profileId: profile_id, 
          hasExistingResume: true,
          // Cache the resume content and filename to persist across navigation
          cachedResumeContent: parsed_content,
          uploadedResumeFileName: file.name,
          // NEW: University detection
          universityDetected: university_detected,
          universityInDatabase: university_in_database
        }));
        
        toast.success('Resume uploaded and integrated with profile successfully');
      } else {
        throw new Error('Backend integration failed');
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to upload and integrate resume: ' + (e.response?.data?.detail || e.message));
    } finally {
      setUploading(false);
    }
  };

  const callDirections = async () => {
    try {
      setLoading(true);
      const merged = { ...(profile || {}), ...state.first };
      
      if (state.profileId) {
        // Profile already exists with resume content, use it directly
        await professionalProfileApi.updateProfile(state.profileId, merged);
        const { data } = await professionalProfileApi.aiDirectionsSync(state.profileId);
        setState(s => ({ ...s, directions: data }));
      } else {
        // No resume uploaded yet, use bootstrap method which will look for existing resumes
        const { data } = await professionalProfileApi.bootstrapAndDirectionsAuto(merged);
        setState(s => ({ ...s, directions: data }));
      }
    } catch (e) {
      console.error(e);
      toast.error('AI direction suggestion failed');
    } finally { setLoading(false); }
  };

  const callEnhancedDirections = async () => {
    try {
      setLoading(true);
      
      let selectedResumeId = null;
      let shouldReplaceProfileData = false;
      
      // Determine which resume to use based on resumeOption
      if (state.resumeOption === 'selectExisting') {
        // User selected a different existing resume - need to fetch content and sync profile
        if (state.selectedResume?.id) {
          // Fetch the first version content using the resume ID
          const resumeResponse = await resumeApi.getFirstResumeVersion(state.selectedResume.id);
          const resumeContent = resumeResponse.data;
          
          if (resumeContent) {
            let profileId = state.profileId;
            
            // If no profile exists, create one using the user input from step 1
            if (!profileId) {
              const createResponse = await professionalProfileApi.createProfile(state.first);
              profileId = createResponse.data.id;
              setState(s => ({ ...s, profileId }));
            }
            
            const syncResponse = await professionalProfileApi.syncFromResume(profileId, resumeContent);
            // Update state with university detection information from the synced profile
            if (syncResponse?.data) {
              setState(s => ({
                ...s,
                universityDetected: syncResponse.data.university_detected || s.universityDetected,
                universityInDatabase: syncResponse.data.university_in_database || s.universityInDatabase
              }));
            }
            // After syncing, the profile will have the new resume data and university detection
            // Continue to generate directions with the updated profile
            // Don't return early - let the function continue to generateCareerDirections()
          } else {
            throw new Error('Could not fetch resume content');
          }
        } else {
          throw new Error('No resume selected');
        }
      } else if (state.resumeOption === 'uploadNew') {
        // For uploaded resume, the profile should already be updated with the new resume data
        // from the handleResumeUpload function, so we can proceed with generating directions
        // No additional action needed
      } else if (state.resumeOption === 'useExisting') {
        // Use existing profile data without resume analysis - load existing directions
        if (profile?.preferences?.career_directions?.length > 0) {
          setState(s => ({ 
            ...s, 
            careerDirections: profile.preferences.career_directions,
            universityDetected: profile.university_detected || s.universityDetected,
            universityInDatabase: profile.university_in_database || s.universityInDatabase
          }));
          setLoading(false);
          // Return university detection info from existing profile
          return {
            universityDetected: profile.university_detected,
            universityInDatabase: profile.university_in_database
          };
        }
      }
      
      const { data } = await professionalProfileApi.generateCareerDirections();
      
      // Update career directions and university detection
      setState(s => ({ 
        ...s, 
        careerDirections: data.directions || [],
        universityDetected: data.university_detected || s.universityDetected,
        universityInDatabase: data.university_in_database || s.universityInDatabase
      }));
      
      // If we should replace profile data and the backend returned new profile data
      if (shouldReplaceProfileData && data.profile_update) {
        // Replace the first step profile data with data from the new resume
        setState(s => ({
          ...s,
          first: {
            ...s.first,
            ...data.profile_update,
            // Preserve any career preferences that were set in step 1
            career_preference: {
              ...data.profile_update.career_preference,
              ...s.first.career_preference
            }
          }
        }));
        toast.success('Profile updated with resume data');
      }
      
      // Return university detection info for immediate use
      return {
        universityDetected: data.university_detected,
        universityInDatabase: data.university_in_database
      };
      
    } catch (e) {
      console.error(e);
      toast.error('AI career direction generation failed');
    } finally { setLoading(false); }
  };

  const callAnalyze = async () => {
    try {
      setLoading(true);
      const merged = { ...(profile || {}), ...state.first };
      const pid = await ensureProfile(merged);
      await professionalProfileApi.updateProfile(pid, merged);
      const { data } = await professionalProfileApi.aiAnalyze(merged, state.chosenDirection);
      
      // Extract AI-generated goals if available
      if (data.goals) {
        setState(s => ({ 
          ...s, 
          analysis: data,
          ai_generated_short_goals: data.goals.short_term,
          ai_generated_long_goals: data.goals.long_term
        }));
      } else {
        setState(s => ({ ...s, analysis: data }));
      }
    } catch (e) {
      console.error(e);
      toast.error('AI analysis failed');
    } finally { setLoading(false); }
  };

  const queryCourses = async () => {
    try {
      const uni = state.universityDetected || profile?.education?.[0]?.school || '';
      if (!uni) return 0;
      const { data } = await professionalProfileApi.getUniversityCoursesEnhanced(uni);
      setState(s => ({ ...s, courseOptions: data || [] }));
      return (data || []).length;
    } catch (e) {
      console.error(e);
      return 0;
    }
  };

  const handleCourseSelection = async (courseName) => {
    try {
      const selected = state.selectedCourses.includes(courseName);
      const newSelected = selected 
        ? state.selectedCourses.filter(c => c !== courseName)
        : [...state.selectedCourses, courseName];
      
      setState(s => ({ ...s, selectedCourses: newSelected }));

      if (!selected) {
        // Get course details from already loaded courseOptions (no need for additional API call)
        const courseData = state.courseOptions.find(course => course.course === courseName);
        if (courseData) {
          setState(s => ({ 
            ...s, 
            selectedCourseDetails: { 
              ...s.selectedCourseDetails, 
              [courseName]: courseData 
            } 
          }));
        }
      }
    } catch (e) {
      console.error(e);
      toast.error('Failed to fetch course details');
    }
  };

  const submitAll = async () => {
    try {
      setSubmitting(true);
      // persist first step to profile immediately
      saveFirstStepToProfile();

      const inputs = {
        professional_goals: state.professional_goals,
        chosen_direction: state.chosenDirection,
        followup_answers: state.followupAnswers,
        selected_courses: state.selectedCourses,
        selected_course_details: state.selectedCourseDetails,
        selected_projects: state.selectedProjects,
        selected_skills: state.selectedSkills,
      };

      const mergedProfile = { ...(profile || {}), ...state.first };
      const { data } = await professionalProfileApi.aiSynthesize(mergedProfile, inputs);

      const newAdditionalDetails = Object.entries(state.followupAnswers || {}).map(([q, a]) => {
        const contextSelections = state.followupContextSelections[q] || {};
        let contextText = '';
        
        if (contextSelections.companies?.length > 0) {
          contextText += contextSelections.companies.map(company => `At ${company}`).join(', ') + ': ';
        }
        if (contextSelections.education?.length > 0) {
          contextText += contextSelections.education.map(school => `At ${school}`).join(', ') + ': ';
        }
        if (contextSelections.courses?.length > 0) {
          contextText += contextSelections.courses.map(school => `At ${school}`).join(', ') + ': ';
        }
        if (contextSelections.recommendedSkills?.length > 0) {
          contextText += `With ${contextSelections.recommendedSkills.join(', ')}: `;
        }
        
        return { 
          question: q, 
          answer: contextText + a
        };
      });
      
      // Append new additional details to existing ones
      const existingAdditionalDetails = Array.isArray(profile?.additional_details) ? profile.additional_details : [];
      const additional_details = [...existingAdditionalDetails, ...newAdditionalDetails];

      const updates = {
        ...mergedProfile,
        additional_details,
        professional_goals: {
          short_term: (state.short_term_goals || '').split('\n').map(x => x.trim()).filter(Boolean),
          long_term: (state.long_term_goals || '').split('\n').map(x => x.trim()).filter(Boolean),
          learning_interests: Array.isArray(profile?.professional_goals?.learning_interests) ? profile.professional_goals.learning_interests : []
        },
      };

      // merge additions from AI
      if (data?.add_courses?.length) {
        const edu = Array.isArray(updates?.education) ? [...updates.education] : [];
        const firstEdu = edu[0] || { school: '', start: '', end: '', major: '', minor: '', gpa: '', courses: [] };
        firstEdu.courses = Array.from(new Set([...(firstEdu.courses || []), ...data.add_courses]));
        if (!edu[0]) edu[0] = firstEdu; else edu[0] = firstEdu;
        updates.education = edu;
      }

      if (data?.add_activities?.length) {
        updates.activities = [...(updates?.activities || []), ...data.add_activities];
      }

      if (data?.add_projects_to_experience?.length) {
        updates.experience = [...(updates?.experience || []), ...data.add_projects_to_experience];
      }

      if (data?.add_skills_grouped?.length) {
        updates.skills = [...(updates?.skills || []), ...data.add_skills_grouped];
      }

      const profileId = state.profileId || profile?._id;
      if (profileId) {
        await professionalProfileApi.updateProfile(profileId, updates);
      } else {
        const created = await professionalProfileApi.createProfile(updates);
        setState(s => ({ ...s, profileId: created?.data?.id }));
      }

      toast.success('Professional profile created successfully!');
      if (reloadProfiles) {
        await reloadProfiles();
      }
      // Update the current profile state with the saved data
      if (updates) {
        onProfileUpdate?.(updates);
      }
      onClose?.();
      // Redirect to profile page after successful completion
      navigate('/profile');
    } catch (e) {
      console.error(e);
      toast.error('Submit failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="bg-white w-full max-w-6xl h-[85vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-50 to-primary-50 px-8 py-6 border-b border-gray-200">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Build Your Professional Profile</h2>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 transition-all duration-200 p-2 hover:bg-gray-100 rounded-full"
            >
              <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center w-full">
            <div className="flex items-center justify-center max-w-2xl mx-auto">
              {([
                { step: 1, label: 'Basic Info', icon: '👤', description: 'Career & location preferences' },
                { step: 2, label: 'Resume', icon: '📄', description: 'Upload your resume' },
                { step: 3, label: 'Courses', icon: '🎓', description: 'Select university courses' }
              ].filter(step => {
                // Show step 3 (Courses) only if university is in database
                if (step.step === 3) {
                  return state.universityInDatabase;
                }
                return true;
              }))
              .map(({ step, label, icon, description }, index, arr) => (
                <div key={step} className="flex items-center">
                  <div className="flex flex-col items-center min-w-0">
                    <div className={`flex items-center justify-center w-12 h-12 rounded-full text-lg font-bold transition-all duration-300 shadow-sm ${
                      state.step >= step 
                        ? 'bg-blue-600 text-white shadow-lg scale-110' 
                        : 'bg-white text-gray-400 border-2 border-gray-200'
                    }`}>
                      {state.step > step ? '✓' : icon}
                    </div>
                    <div className="mt-3 text-center">
                      <div className={`text-sm font-semibold ${state.step >= step ? 'text-blue-600' : 'text-gray-500'}`}>
                        {label}
                      </div>
                      <div className="text-xs text-gray-400 mt-1 max-w-24">
                        {description}
                      </div>
                    </div>
                  </div>
                  {index < arr.length - 1 && (
                    <div className={`w-24 h-1 mx-4 rounded transition-all duration-300 ${
                      state.step > step ? 'bg-blue-600' : state.step === step ? 'bg-blue-400' : 'bg-gray-200'
                    }`}>
                      {state.step === step && loading && (
                        <div className="h-full bg-gradient-to-r from-blue-400 to-blue-600 rounded animate-pulse"></div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            {(loading || uploading) && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <div className="flex-1">
                <div className="text-sm font-medium text-blue-700">
                  {uploading ? 'Processing your resume...' : loading ? 'AI is analyzing your profile...' : 'Processing...'}
                </div>
                <div className="w-full bg-blue-200 rounded-full h-1.5 mt-1">
                  <div className="bg-blue-600 h-1.5 rounded-full animate-pulse" style={{width: uploading ? '60%' : '80%'}}></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {state.step === 1 && (
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Set Your Preferences</h3>
              <p className="text-gray-600">Tell us about your career preferences to get personalized recommendations</p>
            </div>

            <div className="grid gap-8">
            <div>
              <label className="block text-sm font-medium">Visa Status</label>
              <div className="flex space-x-2">
                <select className="mt-1 w-full border rounded px-3 py-2" value={state.first.visa_status} onChange={e => setState(s => ({ ...s, first: { ...s.first, visa_status: e.target.value } }))}>
                  <option value="">Select...</option>
                  {VISA_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                {state.first.visa_status === 'Other' && (
                  <input className="mt-1 w-full border rounded px-3 py-2" placeholder="Specify" onChange={e => setState(s => ({ ...s, first: { ...s.first, visa_status: e.target.value } }))} />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium">Current Location (City, State)</label>
              <div className="grid grid-cols-3 gap-2">
                <input className="mt-1 w-full border rounded px-3 py-2 col-span-2" placeholder="City" value={(state.first.location_preference.current_location||'').split(',')[0] || ''} onChange={e => {
                  const st = (state.first.location_preference.current_location||'').split(',')[1] || '';
                  setState(s => ({ ...s, first: { ...s.first, location_preference: { ...s.first.location_preference, current_location: `${e.target.value}${st?`, ${st.trim()}`:''}` } }}));
                }} />
                <select className="mt-1 w-full border rounded px-3 py-2" value={(state.first.location_preference.current_location||'').split(',')[1]?.trim() || ''} onChange={e => {
                  const city = (state.first.location_preference.current_location||'').split(',')[0] || '';
                  const v = e.target.value === 'Other' ? '' : e.target.value;
                  setState(s => ({ ...s, first: { ...s.first, location_preference: { ...s.first.location_preference, current_location: `${city}${v?`, ${v}`:''}` } }}));
                }}>
                  <option value="">State...</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium">Remote Preference</label>
              <select className="mt-1 w-full border rounded px-3 py-2" value={state.first.career_preference.remote_preference} onChange={e => setState(s => ({ ...s, first: { ...s.first, career_preference: { ...s.first.career_preference, remote_preference: e.target.value } } }))}>
                <option value="">Select...</option>
                {REMOTE_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">MBTI</label>
              <div className="flex space-x-2">
                <select className="mt-1 w-full border rounded px-3 py-2" value={state.first.personality.mbti} onChange={e => {
                  const v = e.target.value;
                  const preset = MBTI_PRESETS[v] || {};
                  setState(s => ({ ...s, first: { ...s.first, personality: { ...s.first.personality, mbti: v, traits: preset.traits || s.first.personality.traits, work_style: preset.work_style || s.first.personality.work_style, leadership_style: preset.leadership_style || s.first.personality.leadership_style } } }));
                }}>
                  <option value="">Select...</option>
                  {MBTI_OPTIONS.map(v => <option key={v} value={v}>{v}</option>)}
                </select>
                {state.first.personality.mbti === 'Other' && (
                  <input className="mt-1 w-full border rounded px-3 py-2" placeholder="Type" onChange={e => setState(s => ({ ...s, first: { ...s.first, personality: { ...s.first.personality, mbti: e.target.value } } }))} />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <input className="w-full border rounded px-3 py-2 bg-gray-100" readOnly placeholder="Traits" value={(state.first.personality.traits||[]).join(', ')} />
              <input className="w-full border rounded px-3 py-2 bg-gray-100" readOnly placeholder="Work style" value={state.first.personality.work_style||''} />
            </div>

            {/* Career Preferences Section */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-8">
              <h4 className="text-lg font-semibold text-blue-800 mb-4">Career Preferences</h4>
              
              <div className="grid gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Desired Job Titles/Roles</label>
                  <input 
                    className="w-full border rounded px-3 py-2" 
                    placeholder="e.g. Data Engineer, Senior Software Developer, Product Manager (comma separated)"
                    value={(state.first.career_preference.desired_roles || []).join(', ')}
                    onChange={e => setState(s => ({ 
                      ...s, 
                      first: { 
                        ...s.first, 
                        career_preference: { 
                          ...s.first.career_preference, 
                          desired_roles: e.target.value.split(',').map(r => r.trim()).filter(r => r) 
                        } 
                      } 
                    }))} 
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Industries of Interest</label>
                  <div className="grid grid-cols-3 gap-2">
                    {INDUSTRIES.map(industry => (
                      <label key={industry} className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          checked={(state.first.career_preference.industries || []).includes(industry)}
                          onChange={e => {
                            const industries = state.first.career_preference.industries || [];
                            const newIndustries = e.target.checked 
                              ? [...industries, industry]
                              : industries.filter(i => i !== industry);
                            setState(s => ({ 
                              ...s, 
                              first: { 
                                ...s.first, 
                                career_preference: { 
                                  ...s.first.career_preference, 
                                  industries: newIndustries 
                                } 
                              } 
                            }));
                          }}
                        />
                        <span className="text-sm">{industry}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Company Size Preference</label>
                    <select 
                      className="w-full border rounded px-3 py-2"
                      value={(state.first.career_preference.company_size || [])[0] || ''}
                      onChange={e => setState(s => ({ 
                        ...s, 
                        first: { 
                          ...s.first, 
                          career_preference: { 
                            ...s.first.career_preference, 
                            company_size: e.target.value ? [e.target.value] : [] 
                          } 
                        } 
                      }))}
                    >
                      <option value="">No preference</option>
                      {COMPANY_SIZES.map(size => (
                        <option key={size} value={size}>{size}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Employment Type</label>
                    <select 
                      className="w-full border rounded px-3 py-2"
                      value={(state.first.career_preference.employment_type || [])[0] || ''}
                      onChange={e => setState(s => ({ 
                        ...s, 
                        first: { 
                          ...s.first, 
                          career_preference: { 
                            ...s.first.career_preference, 
                            employment_type: e.target.value ? [e.target.value] : [] 
                          } 
                        } 
                      }))}
                    >
                      <option value="">No preference</option>
                      {EMPLOYMENT_TYPES.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Career Level</label>
                  <select 
                    className="w-full border rounded px-3 py-2"
                    value={state.first.career_preference.career_level || ''}
                    onChange={e => setState(s => ({ 
                      ...s, 
                      first: { 
                        ...s.first, 
                        career_preference: { 
                          ...s.first.career_preference, 
                          career_level: e.target.value 
                        } 
                      } 
                    }))}
                  >
                    <option value="">Select career level</option>
                    {CAREER_LEVELS.map(level => (
                      <option key={level} value={level}>{level}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Salary ($)</label>
                    <input 
                      type="number" 
                      className="w-full border rounded px-3 py-2"
                      placeholder="e.g. 80000"
                      value={state.first.career_preference.salary_range?.min || ''}
                      onChange={e => setState(s => ({ 
                        ...s, 
                        first: { 
                          ...s.first, 
                          career_preference: { 
                            ...s.first.career_preference, 
                            salary_range: {
                              ...s.first.career_preference.salary_range,
                              min: e.target.value ? parseInt(e.target.value) : null,
                              currency: 'USD',
                              negotiable: true
                            }
                          } 
                        } 
                      }))}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Salary ($)</label>
                    <input 
                      type="number" 
                      className="w-full border rounded px-3 py-2"
                      placeholder="e.g. 120000"
                      value={state.first.career_preference.salary_range?.max || ''}
                      onChange={e => setState(s => ({ 
                        ...s, 
                        first: { 
                          ...s.first, 
                          career_preference: { 
                            ...s.first.career_preference, 
                            salary_range: {
                              ...s.first.career_preference.salary_range,
                              max: e.target.value ? parseInt(e.target.value) : null,
                              currency: 'USD',
                              negotiable: true
                            }
                          } 
                        } 
                      }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Important Benefits (select all that apply)</label>
                  <div className="grid grid-cols-3 gap-2">
                    {BENEFITS.map(benefit => (
                      <label key={benefit} className="flex items-center space-x-2">
                        <input 
                          type="checkbox" 
                          checked={(state.first.career_preference.benefits_priorities || []).includes(benefit)}
                          onChange={e => {
                            const benefits = state.first.career_preference.benefits_priorities || [];
                            const newBenefits = e.target.checked 
                              ? [...benefits, benefit]
                              : benefits.filter(b => b !== benefit);
                            setState(s => ({ 
                              ...s, 
                              first: { 
                                ...s.first, 
                                career_preference: { 
                                  ...s.first.career_preference, 
                                  benefits_priorities: newBenefits 
                                } 
                              } 
                            }));
                          }}
                        />
                        <span className="text-sm">{benefit}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
            </div>

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button 
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 hover:shadow-sm text-gray-700 rounded-lg font-medium transition-all duration-200"
                onClick={onClose}
              >
                Cancel
              </button>
              <button 
                className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200"
                onClick={() => { 
                  saveFirstStepToProfile(); 
                  next(); 
                }}
              >
                Next →
              </button>
            </div>
          </div>
        )}

        {state.step === 2 && (
          <div className="max-w-4xl mx-auto space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Resume Selection</h3>
              <p className="text-gray-600">Choose how you want to proceed with your career direction analysis</p>
            </div>
            
            {/* Resume Selection Options */}
            <div className="space-y-4">
              {/* Option 1: Use existing profile */}
              {hasUsableProfileData(profile) && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="resumeOption"
                      value="useExisting"
                      checked={state.resumeOption === 'useExisting'}
                      onChange={e => setState(s => ({ ...s, resumeOption: e.target.value, selectedResume: null, cachedResumeContent: null }))}
                      className="mt-1 form-radio h-4 w-4 text-primary-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <svg className="w-5 h-5 text-primary-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        <span className="font-medium text-primary-700">Use Existing Profile Data</span>
                      </div>
                      <p className="text-sm text-primary-600">Continue with your saved profile information without using resume data</p>
                    </div>
                  </label>
                </div>
              )}

              {/* Option 2: Select existing resume */}
              {state.hasExistingResume && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="resumeOption"
                      value="selectExisting"
                      checked={state.resumeOption === 'selectExisting'}
                      onChange={e => setState(s => ({ 
                        ...s, 
                        resumeOption: e.target.value, 
                        cachedResumeContent: null,
                        // Clear saved directions when changing to different resume option
                        careerDirections: [],
                        primaryDirection: null,
                        followupAnswers: {},
                        followupContextSelections: {}
                      }))}
                      className="mt-1 form-radio h-4 w-4 text-green-600"
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                        </svg>
                        <span className="font-medium text-green-700">Select Existing Resume</span>
                      </div>
                      <p className="text-sm text-green-600 mb-3">Choose from your uploaded resumes</p>
                      {state.resumeOption === 'selectExisting' && (
                        <ResumeSelector 
                          selectedResume={state.selectedResume}
                          onSelect={(resume) => setState(s => ({ 
                            ...s, 
                            selectedResume: resume,
                            // Clear saved directions when selecting different resume
                            careerDirections: [],
                            primaryDirection: null,
                            followupAnswers: {},
                            followupContextSelections: {}
                          }))}
                        />
                      )}
                    </div>
                  </label>
                </div>
              )}

              {/* Option 3: Upload new resume */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="radio"
                    name="resumeOption"
                    value="uploadNew"
                    checked={state.resumeOption === 'uploadNew'}
                    onChange={e => setState(s => ({ 
                      ...s, 
                      resumeOption: e.target.value, 
                      selectedResume: null,
                      // Clear saved directions when changing to upload new resume
                      careerDirections: [],
                      primaryDirection: null,
                      followupAnswers: {},
                      followupContextSelections: {}
                    }))}
                    className="mt-1 form-radio h-4 w-4 text-blue-600"
                  />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                      <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                      </svg>
                      <span className="font-medium text-blue-700">Upload New Resume</span>
                    </div>
                    <p className="text-sm text-blue-600 mb-3">Upload a new resume file for analysis</p>
                    
                    {state.resumeOption === 'uploadNew' && (
                      <>
                        {state.uploadedResumeFileName && (
                          <p className="text-sm text-green-600 mb-3">✓ Uploaded: {state.uploadedResumeFileName}</p>
                        )}
                        <input 
                          type="file" 
                          accept=".pdf,.doc,.docx" 
                          onChange={e => e.target.files?.[0] && handleResumeUpload(e.target.files[0])} 
                          disabled={uploading}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {uploading && (
                          <div className="mt-2 flex items-center space-x-2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                            <p className="text-sm text-blue-600">Uploading and parsing resume...</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </label>
              </div>
            </div>
            <div className="flex justify-between">
              <button type="button" aria-label="Back" className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all duration-200" onClick={prev}>Back</button>
              <button 
                type="button"
                aria-label="Continue"
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2" 
                disabled={loading || uploading} 
                onClick={async () => { 
                  // Validation based on selected resume option
                  if (state.resumeOption === 'selectExisting') {
                    if (!state.selectedResume) {
                      toast.error('Please select a resume from the list');
                      return;
                    }
                  } else if (state.resumeOption === 'uploadNew') {
                    if (!state.cachedResumeContent) {
                      toast.error('Please upload a new resume file');
                      return;
                    }
                  } else if (state.resumeOption === 'useExisting') {
                    if (!hasUsableProfileData(profile)) {
                      toast.error('No existing profile data found');
                      return;
                    }
                  }
                  
                  // Always call callEnhancedDirections first to detect university
                  const universityInfo = await callEnhancedDirections();
                  
                  // After university detection is complete, navigate based on result
                  if (universityInfo?.universityInDatabase) {
                    // Go to step 3 (Course Selection)
                    next();
                  } else {
                    // For non-targeted university users: finish after resume upload
                    // Show success message and offer career direction
                    toast.success('Profile created successfully!');
                    
                    // Show career direction modal
                    setTimeout(() => {
                      setShowCareerDirectionModal(true);
                    }, 500);
                    return;
                  }
                }}
              >
                {loading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>}
                <span>{loading ? 'Processing...' : 'Continue'}</span>
              </button>
            </div>
          </div>
        )}

        {state.step === 3 && (
          <div className="max-w-6xl mx-auto space-y-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Select University Courses</h3>
              <p className="text-gray-600">Choose the courses you've taken at {state.universityDetected} to enhance your profile</p>
            </div>

            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center space-x-2 mb-3">
                  <svg className="w-5 h-5 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
                  </svg>
                  <span className="font-medium text-blue-700">Course Selection</span>
                </div>
                <p className="text-sm text-blue-600 mb-4">
                  Select all the courses you've taken. This will help AI generate more relevant career directions and skill recommendations.
                </p>
                
                <div className="flex justify-between items-center mb-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        const allCourses = state.courseOptions.map(c => c.course);
                        setState(s => ({ ...s, selectedCourses: allCourses }));
                      }}
                      className="px-3 py-1 text-sm bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-lg transition-all duration-200"
                    >
                      Select All
                    </button>
                    <button
                      onClick={() => setState(s => ({ ...s, selectedCourses: [] }))}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 hover:shadow-sm text-gray-700 rounded-lg transition-all duration-200"
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="text-sm text-gray-600">
                    {state.selectedCourses.length} of {state.courseOptions.length} selected
                  </div>
                </div>
                
                <div className="max-h-96 overflow-y-auto border rounded bg-white p-2">
                  {state.courseOptions.map(course => (
                    <label key={course.id} className="flex items-start space-x-3 p-3 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={state.selectedCourses.includes(course.course)}
                        onChange={() => handleCourseSelection(course.course)}
                        className="mt-1 text-blue-600"
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{course.course}</div>
                        {course.course_code && (
                          <div className="text-sm text-gray-500">{course.course_code}</div>
                        )}
                        <div className="text-xs text-gray-600 mt-1">
                          {course.skills_count} skills • {course.projects_count} projects
                        </div>
                        {course.skills && course.skills.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {course.skills.slice(0, 3).map((skill, idx) => (
                              <span key={idx} className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                {skill}
                              </span>
                            ))}
                            {course.skills.length > 3 && (
                              <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded">
                                +{course.skills.length - 3} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>

            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button 
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 hover:shadow-sm text-gray-700 rounded-lg font-medium transition-all duration-200"
                onClick={prev}
              >
                ← Back
              </button>
              <div className="flex space-x-3">
                <button 
                  className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-gray-700 rounded-lg font-medium transition-all duration-200"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      // Skip course selection and finish profile
                      await callEnhancedDirections();
                      
                      toast.success('Profile created successfully! You can now explore career directions.');
                      onClose?.();
                      
                      pendingTimerRef.current = setTimeout(() => {
                        pendingTimerRef.current = null;
                        if (window.confirm('Would you like to explore career directions now?')) {
                          navigate('/career-direction');
                        }
                      }, 500);
                    } catch (e) {
                      console.error(e);
                      toast.error('Failed to complete profile');
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  {loading ? 'Skipping...' : 'Skip Courses'}
                </button>
                <button 
                  className="px-6 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200"
                onClick={async () => {
                  if (state.selectedCourses.length === 0) {
                    toast.error('Please select at least one course or click "Skip Courses" to continue without courses');
                    return;
                  }
                  
                  // Submit selected courses and regenerate career directions
                  try {
                    setLoading(true);
                    await professionalProfileApi.selectUniversityCourses(
                      state.profileId, 
                      state.universityDetected, 
                      state.selectedCourses
                    );
                    toast.success('Courses selected successfully');
                    
                    // Generate career directions in backend (but don't need to display them here)
                    await callEnhancedDirections();
                    
                    // For targeted university users: finish after course selection
                    toast.success('Profile and courses saved successfully!');
                    
                    // Show career direction modal
                    setTimeout(() => {
                      setShowCareerDirectionModal(true);
                    }, 500);
                  } catch (e) {
                    console.error(e);
                    toast.error('Failed to save course selection');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {loading ? 'Saving...' : `Continue with ${state.selectedCourses.length} courses →`}
              </button>
              </div>
            </div>
          </div>
          </div>

        )}


        {state.step === 5 && (
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Profile Analysis & Questions</h3>
              <p className="text-gray-600">Review your strengths and areas for improvement, then answer follow-up questions</p>
            </div>
            
            <div className="space-y-6">
              {/* Selected Direction Summary */}
              {state.primaryDirection && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4">
                    <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                    </svg>
                    <h4 className="text-lg font-semibold text-blue-800">Selected Direction: {state.primaryDirection.title}</h4>
                  </div>
                  <p className="text-blue-700">{state.primaryDirection.reason}</p>
                </div>
              )}


              
              {/* Follow-up Questions */}
              {state.primaryDirection && (
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h4 className="text-lg font-semibold text-gray-800 mb-4">Follow-up Questions</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Help us understand your experience better by answering these questions about {state.primaryDirection.title}
                  </p>
                  
                  {/* Strengths and Weaknesses Section */}
                  {(state.primaryDirection.strengths?.length > 0 || state.primaryDirection.weaknesses?.length > 0) && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
                      <h4 className="text-lg font-semibold text-gray-800 mb-4">💪 Your Profile Analysis</h4>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {state.primaryDirection.strengths?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-green-700 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                              </svg>
                              Strengths for this role
                            </h5>
                            <ul className="space-y-2">
                              {state.primaryDirection.strengths.map((strength, idx) => (
                                <li key={idx} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-green-500 mr-2 mt-0.5">•</span>
                                  {strength}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        
                        {state.primaryDirection.weaknesses?.length > 0 && (
                          <div>
                            <h5 className="text-sm font-semibold text-orange-700 mb-3 flex items-center">
                              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
                              </svg>
                              Areas to develop
                            </h5>
                            <ul className="space-y-2">
                              {state.primaryDirection.weaknesses.map((weakness, idx) => (
                                <li key={idx} className="text-sm text-gray-700 flex items-start">
                                  <span className="text-orange-500 mr-2 mt-0.5">•</span>
                                  {weakness}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="space-y-4">
                    {(state.primaryDirection.followup_questions || []).map((question, i) => {
                      const questionText = question.question || question;
                      const questionKey = typeof question === 'string' ? question : question.question;
                      const questionContext = question.context || 'general';
                      
                      // Use the context field from the question to determine what selections to show
                      const isExperienceQuestion = questionContext === 'experience';
                      const isSkillsQuestion = questionContext === 'skills';
                      const isEducationQuestion = questionContext === 'education';
                      const isCourseQuestion = questionContext === 'course';
                      
                      // Helper function to build context text
                      const buildContextText = (contextSelections) => {
                        let contextText = '';
                        if (contextSelections.companies?.length > 0) {
                          contextText += contextSelections.companies.map(company => `At ${company}`).join(', ') + ': ';
                        }
                        if (contextSelections.education?.length > 0) {
                          contextText += contextSelections.education.map(school => `At ${school}`).join(', ') + ': ';
                        }
                        if (contextSelections.courses?.length > 0) {
                          contextText += contextSelections.courses.map(school => `At ${school}`).join(', ') + ': ';
                        }
                        if (contextSelections.skills?.length > 0) {
                          contextText += `Using ${contextSelections.skills.join(', ')}: `;
                        }
                        if (contextSelections.recommendedSkills?.length > 0) {
                          contextText += `With ${contextSelections.recommendedSkills.join(', ')}: `;
                        }
                        return contextText;
                      };
                      
                      const contextSelections = state.followupContextSelections[questionKey] || {};
                      const contextText = buildContextText(contextSelections);
                      const baseAnswer = state.followupAnswers[questionKey] || '';
                      const displayValue = contextText + baseAnswer;

                      return (
                        <div key={i} className="bg-gray-50 rounded-lg p-4">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {questionText}
                          </label>
                          
                          {/* Context-aware inputs based on question type */}
                          {isExperienceQuestion && profile?.background?.experience?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant companies from your experience:</p>
                              <div className="flex flex-wrap gap-2">
                                {profile.background.experience.map((exp, expIndex) => (
                                  <label key={expIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                                      checked={state.followupContextSelections[questionKey]?.companies?.includes(exp.company) || false}
                                      onChange={e => {
                                        const companies = state.followupContextSelections[questionKey]?.companies || [];
                                        const updatedCompanies = e.target.checked 
                                          ? [...companies, exp.company]
                                          : companies.filter(c => c !== exp.company);
                                        setState(s => ({
                                          ...s,
                                          followupContextSelections: {
                                            ...s.followupContextSelections,
                                            [questionKey]: {
                                              ...s.followupContextSelections[questionKey],
                                              companies: updatedCompanies
                                            }
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                      {exp.company}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {isSkillsQuestion && state.primaryDirection?.recommended_skills && Array.isArray(state.primaryDirection.recommended_skills) && state.primaryDirection.recommended_skills.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant recommended skills:</p>
                              <div className="flex flex-wrap gap-2">
                                {state.primaryDirection.recommended_skills.map((skill, skillIndex) => (
                                  <label key={skillIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-green-600 rounded border-gray-300"
                                      checked={state.followupContextSelections[questionKey]?.recommendedSkills?.includes(skill) || false}
                                      onChange={e => {
                                        const recommendedSkills = state.followupContextSelections[questionKey]?.recommendedSkills || [];
                                        const updatedRecommendedSkills = e.target.checked 
                                          ? [...recommendedSkills, skill]
                                          : recommendedSkills.filter(s => s !== skill);
                                        setState(s => ({
                                          ...s,
                                          followupContextSelections: {
                                            ...s.followupContextSelections,
                                            [questionKey]: {
                                              ...s.followupContextSelections[questionKey],
                                              recommendedSkills: updatedRecommendedSkills
                                            }
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                      {skill}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {isEducationQuestion && profile?.background?.education?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant education:</p>
                              <div className="flex flex-wrap gap-2">
                                {profile.background.education.map((edu, eduIndex) => (
                                  <label key={eduIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-primary-600 rounded border-gray-300"
                                      checked={state.followupContextSelections[questionKey]?.education?.includes(edu.school) || false}
                                      onChange={e => {
                                        const education = state.followupContextSelections[questionKey]?.education || [];
                                        const updatedEducation = e.target.checked 
                                          ? [...education, edu.school]
                                          : education.filter(s => s !== edu.school);
                                        setState(s => ({
                                          ...s,
                                          followupContextSelections: {
                                            ...s.followupContextSelections,
                                            [questionKey]: {
                                              ...s.followupContextSelections[questionKey],
                                              education: updatedEducation
                                            }
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                      {edu.school} {edu.degree && `- ${edu.degree}`}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          {isCourseQuestion && profile?.background?.education?.length > 0 && (
                            <div className="mb-3">
                              <p className="text-xs text-gray-500 mb-2">Select relevant university/course:</p>
                              <div className="flex flex-wrap gap-2">
                                {profile.background.education.map((edu, eduIndex) => (
                                  <label key={eduIndex} className="inline-flex items-center">
                                    <input
                                      type="checkbox"
                                      className="form-checkbox h-4 w-4 text-primary-600 rounded border-gray-300"
                                      checked={state.followupContextSelections[questionKey]?.courses?.includes(edu.school) || false}
                                      onChange={e => {
                                        const courses = state.followupContextSelections[questionKey]?.courses || [];
                                        const updatedCourses = e.target.checked 
                                          ? [...courses, edu.school]
                                          : courses.filter(s => s !== edu.school);
                                        setState(s => ({
                                          ...s,
                                          followupContextSelections: {
                                            ...s.followupContextSelections,
                                            [questionKey]: {
                                              ...s.followupContextSelections[questionKey],
                                              courses: updatedCourses
                                            }
                                          }
                                        }));
                                      }}
                                    />
                                    <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                      {edu.school} {edu.degree && `- ${edu.degree}`}
                                    </span>
                                  </label>
                                ))}
                              </div>
                            </div>
                          )}

                          <textarea 
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
                            rows={3}
                            placeholder="Share your experience or thoughts..."
                            value={displayValue}
                            onChange={e => {
                              // Remove context text from input value to get clean answer
                              let cleanAnswer = e.target.value;
                              if (contextText && cleanAnswer.startsWith(contextText)) {
                                cleanAnswer = cleanAnswer.substring(contextText.length);
                              }
                              
                              setState(s => ({ 
                                ...s, 
                                followupAnswers: { 
                                  ...s.followupAnswers, 
                                  [questionKey]: cleanAnswer 
                                } 
                              }));
                            }} 
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <div className="flex justify-between pt-6 border-t border-gray-200">
                <button 
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 hover:shadow-sm text-gray-700 rounded-lg font-medium transition-all duration-200"
                  onClick={prev}
                >
                  ← Back
                </button>
                <button 
                  className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200"
                  onClick={async () => {
                    try {
                      setLoading(true);
                      
                      // Update career preferences based on selected direction
                      const existingRoles = state.first.career_preference.desired_roles || [];
                      const selectedDirectionTitle = state.primaryDirection?.title;
                      const titleSafe = String(selectedDirectionTitle ?? '').trim();
                      
                      // Add the selected direction title to desired roles if not already there
                      const updatedRoles = !titleSafe || existingRoles.includes(titleSafe)
                        ? existingRoles
                        : [...existingRoles, titleSafe];
                      
                      const tl = titleSafe.toLowerCase();
                      const careerUpdates = {
                        career_preference: {
                          ...state.first.career_preference,
                          desired_roles: updatedRoles,
                          career_level: tl.includes('senior') ? 'Senior' :
                                      tl.includes('lead') ? 'Lead' :
                                      tl.includes('principal') ? 'Principal' :
                                      tl.includes('manager') ? 'Manager' :
                                      tl.includes('director') ? 'Director' : 'Mid-Level'
                        },
                        // Also save the primary direction info at the top level
                        primary_career_direction: {
                          title: state.primaryDirection.title,
                          id: state.primaryDirection.id,
                          reason: state.primaryDirection.reason,
                          relevance_score: state.primaryDirection.relevance_score
                        }
                      };
                      
                      // Update profile with career preferences
                      await professionalProfileApi.updateProfile(state.profileId, careerUpdates);
                      
                      // Set primary direction
                      await professionalProfileApi.setPrimaryDirection(state.profileId, state.primaryDirection.id);
                      
                      // Submit follow-up responses with context text included
                      const responses = (state.primaryDirection.followup_questions || []).map(q => {
                        const questionKey = typeof q === 'string' ? q : q.question;
                        const contextSelections = state.followupContextSelections[questionKey] || {};
                        
                        // Build context text for final answer
                        let contextText = '';
                        if (contextSelections.companies?.length > 0) {
                          contextText += contextSelections.companies.map(company => `At ${company}`).join(', ') + ': ';
                        }
                        if (contextSelections.education?.length > 0) {
                          contextText += contextSelections.education.map(school => `At ${school}`).join(', ') + ': ';
                        }
                        if (contextSelections.skills?.length > 0) {
                          contextText += `Using ${contextSelections.skills.join(', ')}: `;
                        }
                        if (contextSelections.recommendedSkills?.length > 0) {
                          contextText += `With ${contextSelections.recommendedSkills.join(', ')}: `;
                        }
                        
                        const finalAnswer = contextText + (state.followupAnswers[questionKey] || '');
                        
                        return {
                          answer: finalAnswer,
                          context: q.context || '',
                          contextSelections: contextSelections // Include selected companies, skills, education
                        };
                      });
                      
                      await professionalProfileApi.submitFollowupResponses(
                        state.profileId, 
                        state.primaryDirection.id, 
                        responses
                      );
                      
                      // Finalize profile
                      await professionalProfileApi.finalizeProfile(state.profileId);
                      
                      toast.success('Profile saved successfully!');
                      
                      // Reload profiles to reflect the updated career directions
                      if (reloadProfiles) {
                        await reloadProfiles();
                      }
                      
                      // Close the modal after successful save
                      onClose?.();
                    } catch (e) {
                      console.error(e);
                      toast.error('Failed to save profile');
                    } finally {
                      setLoading(false);
                    }
                  }}
                >
                  {loading ? 'Saving Profile...' : 'Save Profile & Complete'}
                </button>
              </div>
            </div>
          </div>

        )}

        {/* Additional Direction Selection after profile completion */}
        {state.showAdditionalDirections && (
          <div className="max-w-6xl mx-auto mt-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">🎯 Add More Career Directions</h3>
              <p className="text-gray-600">Select additional career paths you're interested in exploring</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {state.careerDirections
                .filter(direction => !direction.is_primary) // Only show non-primary directions
                .map((direction, i) => (
                <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
                     onClick={() => {
                       // Set this direction as a new primary and show followup questions
                       setState(s => ({ 
                         ...s, 
                         primaryDirection: direction,
                         followupAnswers: {},
                         followupContextSelections: {},
                         showAdditionalDirections: false,
                         step: 5.5 // Special step for additional direction followup
                       }));
                     }}>
                  <div className="flex justify-between items-start mb-4">
                    <h4 className="text-lg font-semibold text-gray-800">{direction.title}</h4>
                    <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                      {direction.relevance_score > 1 ? Math.round(direction.relevance_score) : Math.round(direction.relevance_score * 100)}% match
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">{direction.reason}</p>
                  
                  {/* Show Goals */}
                  {direction.goals?.short_term?.length > 0 && (
                    <div className="mb-4">
                      <h5 className="text-sm font-medium text-gray-700 mb-2">Goals:</h5>
                      <ul className="text-sm text-gray-600 space-y-1">
                        {direction.goals.short_term.slice(0, 3).map((goal, idx) => (
                          <li key={idx} className="flex items-start">
                            <span className="text-blue-500 mr-2">•</span>
                            {goal}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="flex items-center text-blue-600 text-sm font-medium">
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Select this direction
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="flex justify-center space-x-4">
              <button 
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 hover:shadow-sm text-gray-700 rounded-lg font-medium transition-all duration-200"
                onClick={() => {
                  onClose?.();
                  navigate('/profile');
                }}
              >
                Finish & Go to Profile
              </button>
              
              {state.careerDirections.filter(d => !d.is_primary).length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <p>All available directions have been selected.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Additional Direction Followup Questions (Step 5.5) */}
        {state.step === 5.5 && state.primaryDirection && (
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Additional Questions</h3>
              <p className="text-gray-600">Answer follow-up questions about {state.primaryDirection.title}</p>
            </div>
            
            <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Follow-up Questions</h4>
              <p className="text-sm text-gray-600 mb-4">
                Help us understand your experience better by answering these questions about {state.primaryDirection.title}
              </p>
              <div className="space-y-4">
                {(state.primaryDirection.followup_questions || []).map((question, i) => {
                  const questionText = question.question || question;
                  const questionKey = typeof question === 'string' ? question : question.question;
                  const questionContext = question.context || 'general';
                  
                  // Use the context field from the question to determine what selections to show
                  const isExperienceQuestion = questionContext === 'experience';
                  const isSkillsQuestion = questionContext === 'skills';
                  const isEducationQuestion = questionContext === 'education';
                  const isCourseQuestion = questionContext === 'course';

                  return (
                    <div key={i} className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {questionText}
                      </label>
                      
                      {/* Context-aware inputs (same as step 5) */}
                      {isExperienceQuestion && profile?.background?.experience?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-2">Select relevant companies from your experience:</p>
                          <div className="flex flex-wrap gap-2">
                            {profile.background.experience.map((exp, expIndex) => (
                              <label key={expIndex} className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-blue-600 rounded border-gray-300"
                                  checked={state.followupContextSelections[questionKey]?.companies?.includes(exp.company) || false}
                                  onChange={e => {
                                    const companies = state.followupContextSelections[questionKey]?.companies || [];
                                    const updatedCompanies = e.target.checked 
                                      ? [...companies, exp.company]
                                      : companies.filter(c => c !== exp.company);
                                    setState(s => ({
                                      ...s,
                                      followupContextSelections: {
                                        ...s.followupContextSelections,
                                        [questionKey]: {
                                          ...s.followupContextSelections[questionKey],
                                          companies: updatedCompanies
                                        }
                                      }
                                    }));
                                  }}
                                />
                                <span className="ml-2 text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                                  {exp.company}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {isSkillsQuestion && state.primaryDirection?.recommended_skills && Array.isArray(state.primaryDirection.recommended_skills) && state.primaryDirection.recommended_skills.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-2">Select relevant recommended skills:</p>
                          <div className="flex flex-wrap gap-2">
                            {state.primaryDirection.recommended_skills.map((skill, skillIndex) => (
                              <label key={skillIndex} className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-green-600 rounded border-gray-300"
                                  checked={state.followupContextSelections[questionKey]?.recommendedSkills?.includes(skill) || false}
                                  onChange={e => {
                                    const recommendedSkills = state.followupContextSelections[questionKey]?.recommendedSkills || [];
                                    const updatedRecommendedSkills = e.target.checked 
                                      ? [...recommendedSkills, skill]
                                      : recommendedSkills.filter(s => s !== skill);
                                    setState(s => ({
                                      ...s,
                                      followupContextSelections: {
                                        ...s.followupContextSelections,
                                        [questionKey]: {
                                          ...s.followupContextSelections[questionKey],
                                          recommendedSkills: updatedRecommendedSkills
                                        }
                                      }
                                    }));
                                  }}
                                />
                                <span className="ml-2 text-sm bg-green-100 text-green-800 px-2 py-1 rounded-full">
                                  {skill}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {isEducationQuestion && profile?.background?.education?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-2">Select relevant education:</p>
                          <div className="flex flex-wrap gap-2">
                            {profile.background.education.map((edu, eduIndex) => (
                              <label key={eduIndex} className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-primary-600 rounded border-gray-300"
                                  checked={state.followupContextSelections[questionKey]?.education?.includes(edu.school) || false}
                                  onChange={e => {
                                    const education = state.followupContextSelections[questionKey]?.education || [];
                                    const updatedEducation = e.target.checked 
                                      ? [...education, edu.school]
                                      : education.filter(s => s !== edu.school);
                                    setState(s => ({
                                      ...s,
                                      followupContextSelections: {
                                        ...s.followupContextSelections,
                                        [questionKey]: {
                                          ...s.followupContextSelections[questionKey],
                                          education: updatedEducation
                                        }
                                      }
                                    }));
                                  }}
                                />
                                <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                  {edu.school} {edu.degree && `- ${edu.degree}`}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      {isCourseQuestion && profile?.background?.education?.length > 0 && (
                        <div className="mb-3">
                          <p className="text-xs text-gray-500 mb-2">Select relevant university/course:</p>
                          <div className="flex flex-wrap gap-2">
                            {profile.background.education.map((edu, eduIndex) => (
                              <label key={eduIndex} className="inline-flex items-center">
                                <input
                                  type="checkbox"
                                  className="form-checkbox h-4 w-4 text-primary-600 rounded border-gray-300"
                                  checked={state.followupContextSelections[questionKey]?.courses?.includes(edu.school) || false}
                                  onChange={e => {
                                    const courses = state.followupContextSelections[questionKey]?.courses || [];
                                    const updatedCourses = e.target.checked 
                                      ? [...courses, edu.school]
                                      : courses.filter(s => s !== edu.school);
                                    setState(s => ({
                                      ...s,
                                      followupContextSelections: {
                                        ...s.followupContextSelections,
                                        [questionKey]: {
                                          ...s.followupContextSelections[questionKey],
                                          courses: updatedCourses
                                        }
                                      }
                                    }));
                                  }}
                                />
                                <span className="ml-2 text-sm bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                  {edu.school} {edu.degree && `- ${edu.degree}`}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}

                      <textarea 
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500" 
                        rows={3}
                        placeholder="Share your experience or thoughts..."
                        value={state.followupAnswers[questionKey] || ''}
                        onChange={e => setState(s => ({ 
                          ...s, 
                          followupAnswers: { 
                            ...s.followupAnswers, 
                            [questionKey]: e.target.value 
                          } 
                        }))} 
                      />
                    </div>
                  );
                })}
              </div>
            </div>
            
            <div className="flex justify-between pt-6 border-t border-gray-200">
              <button 
                className="px-6 py-3 bg-gray-100 hover:bg-gray-200 hover:shadow-sm text-gray-700 rounded-lg font-medium transition-all duration-200"
                onClick={() => setState(s => ({ ...s, showAdditionalDirections: true, step: 5 }))}
              >
                ← Back to Directions
              </button>
              <button 
                className="px-8 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200"
                onClick={async () => {
                  try {
                    setLoading(true);
                    
                    // Set this direction as primary
                    await professionalProfileApi.setPrimaryDirection(state.profileId, state.primaryDirection.id);
                    
                    // Submit follow-up responses
                    const responses = (state.primaryDirection.followup_questions || []).map(q => {
                      const questionKey = typeof q === 'string' ? q : q.question;
                      const contextSelections = state.followupContextSelections[questionKey] || {};
                      return {
                        answer: state.followupAnswers[questionKey] || '',
                        context: q.context || '',
                        contextSelections: contextSelections
                      };
                    });
                    
                    await professionalProfileApi.submitFollowupResponses(
                      state.profileId, 
                      state.primaryDirection.id, 
                      responses
                    );
                    
                    toast.success('Additional direction added successfully!');
                    
                    // Reload profiles to reflect the updated career directions
                    if (reloadProfiles) {
                      await reloadProfiles();
                    }
                    
                    // Go back to additional directions selection
                    setState(s => ({ 
                      ...s, 
                      showAdditionalDirections: true, 
                      step: 5,
                      primaryDirection: null,
                      followupAnswers: {},
                      followupContextSelections: {}
                    }));
                    
                  } catch (e) {
                    console.error(e);
                    toast.error('Failed to add additional direction');
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {loading ? 'Adding Direction...' : 'Add This Direction'}
              </button>
            </div>
          </div>
        )}

          </div>
        </div>
      </div>
      
      {/* Career Direction Modal */}
      {showCareerDirectionModal && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Profile Created Successfully!
              </h3>
              
              <p className="text-gray-600 mb-6">
                Your professional profile has been saved. Would you like to explore personalized career directions based on your background?
              </p>
              
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowCareerDirectionModal(false);
                    onClose?.();
                  }}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 hover:shadow-sm text-gray-700 rounded-lg font-medium transition-all duration-200"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => {
                    setShowCareerDirectionModal(false);
                    onClose?.();
                    navigate('/career-direction?showDirectionSelection=true');
                  }}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-all duration-200"
                >
                  Explore Careers
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
}
